import type { Engine } from '../components/report/types';

export const PROMPT_CATEGORIES = [
  'competitive',
  'visibility',
] as const;
export type PromptCategory = (typeof PROMPT_CATEGORIES)[number];

export const EXTRACTOR_CATEGORIES = [
  'competitive',
  'visibility',
] as const;

export const CATEGORY_LABELS: Record<PromptCategory, string> = {
  competitive: 'Competitive',
  visibility: 'Visibility',
};

// ---------------------------------------------------------------------------
// Prompt intent — what KIND of buyer question a prompt is.
//
// The SECOND axis. Category (above) decides how a prompt is scored and shows up
// in the Result column as W/L/T vs Yes/No; intent is what a customer opens the
// library to audit ("do I have alternatives queries?"). Surfaces show intent,
// not both — category is derivable from it (head_to_head is competitive,
// everything else organic). Mirrors core/prompts/categories.py; keep in step.
// ---------------------------------------------------------------------------

export const PROMPT_INTENTS = [
  'discovery',
  'alternatives',
  'head_to_head',
  'use_case',
  'problem_aware',
  'thought_leadership',
] as const;
export type PromptIntent = (typeof PROMPT_INTENTS)[number];

export const INTENT_LABELS: Record<PromptIntent, string> = {
  discovery: 'Discovery',
  alternatives: 'Alternatives',
  head_to_head: 'Head-to-head',
  use_case: 'Use case',
  problem_aware: 'Problem',
  thought_leadership: 'Thought leadership',
};

/** Label for a stored intent, degrading to a readable slug for an unknown value
 *  rather than rendering blank. */
export function intentLabel(intent: string | null | undefined): string | null {
  if (!intent) return null;
  return INTENT_LABELS[intent as PromptIntent] ?? intent.replace(/_/g, ' ');
}

// ---------------------------------------------------------------------------
// Coverage tier — canonical order and display labels
// ---------------------------------------------------------------------------

export type CoverageTier = 'primary' | 'substantial' | 'peripheral' | 'incidental' | 'absent';

export const COVERAGE_ORDER: CoverageTier[] = [
  'primary',
  'substantial',
  'peripheral',
  'incidental',
  'absent',
];

export const COVERAGE_LABELS: Record<CoverageTier, string> = {
  primary: 'Primary',
  substantial: 'Substantial',
  peripheral: 'Peripheral',
  incidental: 'Incidental',
  absent: 'Not mentioned',
};

// ---------------------------------------------------------------------------
// Source-detail citation-effectiveness label
// ---------------------------------------------------------------------------

/** THE citation-effectiveness label — /sources table column and the detail-header
 *  tile both read this one constant (Max, 2026-07-24: the long "Citation
 *  effectiveness" header scrunched the table; the tooltip/hint carries the full
 *  explanation, the label stays short). */
export const EFFECTIVENESS_LABEL = 'Effectiveness';

/** THE one sentence that explains it, so the short label is never undefined
 *  wherever it appears. Paired with `EFFECTIVENESS_LABEL` in a `TooltipHeader`
 *  (`{label, content}`) — as a `DataTable` column `header` (which then MUST also
 *  declare `headerLabel`) and as the source-detail `StatCell` tile's `label`.
 *
 *  Why it lives here: the explanation shipped on the detail-page TILE only, so
 *  the same column was documented on one surface and bare on the other three
 *  (`/sources` URL list, "Your pages", the source-detail Co-cited table) — a
 *  0–100 composite with no stated basis (rule 47: same column, same affordance
 *  everywhere). Keep the wording in ONE place so the surfaces can't drift. */
export const EFFECTIVENESS_HINT =
  'Observed 0–100 score blending whether citing answers carry your brand through and whether competitive answers go your way. Thin samples are pulled toward the workspace average.';

// ---------------------------------------------------------------------------
// Competitive outcome — canonical values and base display labels
// ---------------------------------------------------------------------------

export type CompetitiveOutcome = 'we_win' | 'competitor_wins' | 'tie' | 'not_compared';

/**
 * Base labels only. Consumers that append competitor name do so themselves.
 *
 * `not_compared` says what it MEANS, not "N/A" (2026-07-26). The abbreviation was
 * the only placeholder string left in the label layer — it reads as "we failed to
 * load this", which is exactly the reading rule 21 bans placeholders for, and it
 * escaped `check-canonical` CHECK 4 only because a placeholder behind an object
 * KEY is invisible to the JSX-shaped check. It surfaced as a MATRIX AXIS LABEL on
 * the period Changes tab — an `N/A` row and an `N/A` column, where the axis is the
 * one place a value has to name itself.
 *
 * `EvalTable`'s Result column already used "Not compared" as this state's human
 * label (its own `RESULT_LABELS`, for the filter option), so this aligns the
 * canonical map with the wording the product had already settled on. That column
 * keeps rendering the CELL blank — a three-word label does not belong in a column
 * of single letters — which is a cell-density decision, not a second vocabulary.
 */
export const OUTCOME_LABELS: Record<CompetitiveOutcome, string> = {
  we_win: 'Win',
  competitor_wins: 'Loss',
  tie: 'Tie',
  not_compared: 'Not compared',
};

// ---------------------------------------------------------------------------
// Prompt tags
// ---------------------------------------------------------------------------

export type PromptTagKind = 'category' | 'competitor' | 'feature' | 'persona' | 'topic' | 'subject';
export interface PromptTag {
  kind: PromptTagKind;
  value: string;
  label?: string | null;
  domain?: string | null;
}

export function buildPromptTags(opts: {
  subject_name?: string | null;
  competitor_name?: string | null;
  feature_name?: string | null;
  persona_label?: string | null;
  topic_name?: string | null;
  prompt_category?: string | null;
  competitor_domain?: string | null;
  subject_kind?: string | null;
}): PromptTag[] {
  const categoryLabel = opts.prompt_category
    ? CATEGORY_LABELS[opts.prompt_category as PromptCategory] ?? opts.prompt_category
    : null;
  const tags: PromptTag[] = [];
  if (categoryLabel) tags.push({ kind: 'category', value: categoryLabel });
  if (opts.competitor_name)
    tags.push({ kind: 'competitor', value: opts.competitor_name, domain: opts.competitor_domain });
  if (opts.feature_name) tags.push({ kind: 'feature', value: opts.feature_name, label: 'Capability' });
  if (opts.persona_label)
    tags.push({ kind: 'persona', value: opts.persona_label, label: 'Buyer role' });
  if (opts.topic_name) tags.push({ kind: 'topic', value: opts.topic_name, label: 'Topic' });
  if (opts.subject_name)
    tags.push({
      kind: 'subject',
      value: opts.subject_name,
      label: opts.subject_kind === 'product' ? 'product' : null,
    });
  return tags;
}

export const ENGINE_LABELS: Record<string, string> = {
  chatgpt: 'ChatGPT',
  'chatgpt-api': 'ChatGPT',
  claude: 'Claude',
  'claude-api': 'Claude',
  perplexity: 'Perplexity',
  gemini: 'Gemini',
};

export const ENGINES: Engine[] = ['chatgpt', 'claude', 'perplexity', 'gemini'];
